import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ALPINE_CART_BRIDGE_LOAD_GRACE_MS,
  ALPINE_CART_BRIDGE_MOUNT_TIMEOUT_MS
} from 'utils/alpine-cart-bridge';
import {
  AlpineCartBridgeStatus,
  AlpineCartBridgeStatusWaiter
} from 'utils/alpine-cart-bridge-types';

export default function useAlpineCartBridgeStatus() {
  const [status, setStatus] = useState<AlpineCartBridgeStatus>('loading');
  const statusRef = useRef<AlpineCartBridgeStatus>('loading');
  const waitersRef = useRef<AlpineCartBridgeStatusWaiter[]>([]);
  const loadGraceTimerRef = useRef<number>();

  const settle = useCallback((next: AlpineCartBridgeStatus) => {
    statusRef.current = next;
    setStatus(next);
    const waiters = waitersRef.current;
    waitersRef.current = [];
    waiters.forEach((resolve) => resolve(next));
  }, []);

  const markUnavailableIfLoading = useCallback(() => {
    if (statusRef.current === 'loading') settle('unavailable');
  }, [settle]);

  const markIframeLoaded = useCallback(() => {
    window.clearTimeout(loadGraceTimerRef.current);
    loadGraceTimerRef.current = window.setTimeout(
      markUnavailableIfLoading,
      ALPINE_CART_BRIDGE_LOAD_GRACE_MS
    );
  }, [markUnavailableIfLoading]);

  const waitForSettled = useCallback(
    () =>
      new Promise<AlpineCartBridgeStatus>((resolve) => {
        if (statusRef.current === 'loading') {
          waitersRef.current.push(resolve);
        } else {
          resolve(statusRef.current);
        }
      }),
    []
  );

  useEffect(() => {
    const mountTimer = window.setTimeout(
      markUnavailableIfLoading,
      ALPINE_CART_BRIDGE_MOUNT_TIMEOUT_MS
    );
    return () => {
      window.clearTimeout(mountTimer);
      window.clearTimeout(loadGraceTimerRef.current);
    };
  }, [markUnavailableIfLoading]);

  return { status, settle, markIframeLoaded, waitForSettled };
}
